"use client";

import { deleteToken, getToken, onMessage } from "firebase/messaging";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { removePushToken, savePushToken } from "@/app/notifications/_actions";
import { authClient } from "@/lib/auth-client";
import { getFirebaseMessaging } from "@/lib/firebase";
const FCM_ENABLED_STORAGE_KEY = "fcm_enabled";
const FCM_TOKEN_STORAGE_KEY = "fcm_token";
const FCM_PREFERENCE_EVENT = "clutch:fcm-preference-change";
const asRoute = (path: string) => path as Route;
function readNotificationPreference() {
  if (typeof window === "undefined") {
    return true;
  }
  const savedPreference = localStorage.getItem(FCM_ENABLED_STORAGE_KEY);
  return savedPreference === null ? true : savedPreference === "true";
}
function writeNotificationPreference(enabled: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(FCM_ENABLED_STORAGE_KEY, String(enabled));
  window.dispatchEvent(new CustomEvent(FCM_PREFERENCE_EVENT, {
    detail: {
      enabled
    }
  }));
}
export function useFCM(options: {
  preventInit?: boolean;
} = {}) {
  const {
    data: session
  } = authClient.useSession();
  const router = useRouter();
  const userId = session?.user?.id ?? null;
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const tokenSentRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const notificationsSupported = "Notification" in window;
    setIsSupported(notificationsSupported);
    setIsEnabled(readNotificationPreference());
    setHasLoadedPreference(true);
    if (notificationsSupported) {
      setPermission(window.Notification.permission);

      // Listen for permission changes via Permissions API
      if (navigator.permissions) {
        navigator.permissions.query({
          name: "notifications"
        }).then(status => {
          status.onchange = () => {
            setPermission(window.Notification.permission);
          };
        });
      }
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== FCM_ENABLED_STORAGE_KEY) {
        return;
      }
      setIsEnabled(readNotificationPreference());
    };
    const handlePreferenceChange = (event_0: Event) => {
      const detail = (event_0 as CustomEvent<{
        enabled?: boolean;
      }>).detail;
      setIsEnabled(detail?.enabled ?? readNotificationPreference());
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setRefreshTick(tick => tick + 1);
      }
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener(FCM_PREFERENCE_EVENT, handlePreferenceChange);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(FCM_PREFERENCE_EVENT, handlePreferenceChange);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
  const requestPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const status_0 = await Notification.requestPermission();
      setPermission(status_0);
      return status_0;
    } catch (error) {
      console.error("Error requesting permission:", error);
      return "denied";
    }
  };
  const enable = async () => {
    writeNotificationPreference(true);
    setIsEnabled(true);
  };
  useEffect(() => {
    if (isEnabled) {
      return;
    }
    setFcmToken(null);
    tokenSentRef.current = null;
    userIdRef.current = null;
  }, [isEnabled]);
  useEffect(() => {
    if (!userIdRef.current) return;
    if (userIdRef.current === userId) return;
    tokenSentRef.current = null;
    userIdRef.current = null;
    setFcmToken(null);
  }, [userId]);
  useEffect(() => {
    let unsubscribeOnMessage: (() => void) | undefined;
    let isDisposed = false;
    async function setupFCM() {
      if (options.preventInit || !userId || permission !== "granted" || !hasLoadedPreference || !isEnabled) return;
      console.log("[FCM] Initializing...");
      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.error("[FCM] Messaging not supported");
        toast.error("Push notifications are not supported on this device/browser.");
        return;
      }
      try {
        let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
        if ("serviceWorker" in navigator) {
          serviceWorkerRegistration = await navigator.serviceWorker.ready;
        }
        const vapidKey = process.env.NEXT_PUBLIC_VAPID_KEY;
        if (!vapidKey) {
          console.error("[FCM] Missing NEXT_PUBLIC_VAPID_KEY");
          toast.error("Push notification configuration is missing (VAPID Key).");
          return;
        }
        console.log("[FCM] Getting token...");
        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration
        });
        if (token) {
          // Check if we already sent this token for this user
          if (token !== tokenSentRef.current || userId !== userIdRef.current) {
            console.log("[FCM] Token generated, saving to server...", `${token.slice(0, 10)}...`);
            const result = await savePushToken(token, "web");
            if (result?.success) {
              console.log("[FCM] Token saved successfully");
              tokenSentRef.current = token;
              userIdRef.current = userId;
              setFcmToken(token);
              localStorage.setItem(FCM_ENABLED_STORAGE_KEY, "true");
              localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
            } else {
              console.error("[FCM] Failed to save token to server");
              if (result?.message) {
                toast.error(result.message);
              }
            }
          }
        } else {
          console.warn("[FCM] No token received");
        }
        const unsubscribe = onMessage(messaging, payload => {
          console.log("Foreground message received:", payload);
          const data = payload.data ?? {};
          const title = data.title || "New Notification";
          const body = data.body;
          const link = data.link;
          window.dispatchEvent(new CustomEvent("navi:notification"));
          toast(title, {
            description: body,
            action: link ? {
              label: "View",
              onClick: () => {
                router.push(asRoute(link));
              }
            } : undefined
          });
        });
        if (isDisposed) {
          unsubscribe();
          return;
        }
        unsubscribeOnMessage = unsubscribe;
      } catch (error_0) {
        console.error("FCM setup failed:", error_0);
        toast.error("Failed to enable notifications", {
          description: error_0 instanceof Error ? error_0.message : "Unknown error occurred"
        });
      }
    }
    setupFCM();
    return () => {
      isDisposed = true;
      unsubscribeOnMessage?.();
    };
  }, [hasLoadedPreference, isEnabled, options.preventInit, permission, refreshTick, router, userId]);
  const cleanupForLogout = async () => {
    const tokenToRemove = fcmToken || (typeof window !== "undefined" ? localStorage.getItem(FCM_TOKEN_STORAGE_KEY) : null);
    if (tokenToRemove) {
      try {
        await removePushToken(tokenToRemove);
        console.log("[FCM] Token removed from server");
      } catch (error_1) {
        console.error("[FCM] Failed to remove token:", error_1);
      }
    }
    try {
      const messaging_0 = await getFirebaseMessaging();
      if (messaging_0) {
        await deleteToken(messaging_0);
      }
    } catch (error_2) {
      console.error("[FCM] Failed to delete Firebase token:", error_2);
    }
    if (typeof window !== "undefined") {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    }
    setFcmToken(null);
    tokenSentRef.current = null;
    userIdRef.current = null;
  };
  const unregister = async () => {
    await cleanupForLogout();
    writeNotificationPreference(false);
    setIsEnabled(false);
  };
  return {
    cleanupForLogout,
    enable,
    isEnabled,
    isSupported,
    permission,
    requestPermission,
    unregister
  };
}